import {Component, inject} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {DeviceService} from "../../service/device.service";
import {ImeiInfo} from "../../model/imeiInfo";
import {Region} from "../../utils/RegionConstants"
import {formatInvalidImeiStatuses, InvalidImeiMessages} from "../../utils/ImeiInvalidStatusFormatter";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {NgForOf, NgIf} from "@angular/common";
import {ValidateImeiResponse} from "../../model/validateImeiResponse";
import {IridiumValidationResponse} from "../../model/iridiumValidationResponse";

type ValidationTab = 'hubGarmin' | 'iridium';

@Component({
  selector: 'app-validate-imei',
  standalone: true,
  imports: [
    FormsModule,
    CdkTextareaAutosize,
    NgIf,
    NgForOf,
    ReactiveFormsModule
  ],
  templateUrl: './validate-imei.component.html',
  styleUrl: './validate-imei.component.css'
})
export class ValidateImeiComponent {
  private static readonly MAX_IRIDIUM_IMEIS = 10;
  deviceService = inject(DeviceService);

  selectedValidationTab: ValidationTab = 'hubGarmin';

  imeisString: string | null = null;
  imeiResults: ValidateImeiResponse[] = [];

  iridiumImeisString: string | null = null;
  iridiumResults: IridiumValidationResponse[] = [];
  iridiumValidationDisabled = false;
  iridiumValidationError: string | null = null;

  validationDisabled = false;
  imeiValidationResult = false;

  // errors to display under IMEI input
  malformedImeiMessage: string | null = null;
  notFoundImeiMessage: string | null = null;
  deactivateImeiMessage: string | null = null;
  activeOnHubMessage: string | null = null;
  selectedRegion: Region = Region.US;

  Region = Region;

  selectValidationTab(tab: ValidationTab) {
    this.selectedValidationTab = tab;
  }

  public validateImeis() {
    const imeisArray = this.parseImeis(this.imeisString);

    // don't allow user to click validate button while request is being made
    this.validationDisabled = true;
    this.deviceService
      .validateImeis(imeisArray ? imeisArray : [], this.selectedRegion)
      .subscribe(responses => {
        this.imeiResults = responses;
        this.processImeiStatuses(responses.map(response => response.imeiInfo));
        this.validationDisabled = false;
    });
  }

  public validateIridiumImeis() {
    const imeisArray = this.parseImeis(this.iridiumImeisString);
    this.iridiumValidationError = null;
    this.iridiumResults = [];

    if (imeisArray.length > ValidateImeiComponent.MAX_IRIDIUM_IMEIS) {
      this.iridiumValidationError = `Iridium validation is limited to ${ValidateImeiComponent.MAX_IRIDIUM_IMEIS} IMEIs.`;
      return;
    }

    this.iridiumValidationDisabled = true;
    this.deviceService
      .validateIridiumImeis(imeisArray)
      .subscribe({
        next: responses => {
          this.iridiumResults = responses;
          this.iridiumValidationDisabled = false;
        },
        error: error => {
          this.iridiumValidationError = error.error || 'Unable to validate Iridium IMEIs.';
          this.iridiumValidationDisabled = false;
        }
      });
  }

  private processImeiStatuses(imeis: ImeiInfo[]) {
    let invalidImeiMessages: InvalidImeiMessages = formatInvalidImeiStatuses(imeis);
    this.malformedImeiMessage = invalidImeiMessages.malformedImeiMessage;
    this.notFoundImeiMessage = invalidImeiMessages.notFoundImeiMessage;
    this.deactivateImeiMessage = invalidImeiMessages.deactivateImeiMessage;
    this.activeOnHubMessage = invalidImeiMessages.activeOnHubMessage;
    this.imeiValidationResult = !this.hasErrorMessage();
  }

  hasErrorMessage() {
    return !!this.malformedImeiMessage || !!this.notFoundImeiMessage || !!this.deactivateImeiMessage || !!this.activeOnHubMessage;
  }

  hasIridiumAccountResults() {
    return this.iridiumResults.some(response =>
      !!response.accountStatus && response.accountStatus !== 'NOT_FOUND'
    );
  }

  // Split comma, whitespace, new line input, trim entries and dedup.
  private parseImeis(imeisString: string | null): string[] {
    return Array.from(new Set((imeisString || '')
      .split(/[\n\s\t,]/)
      .map(imei => imei.trim())
      .filter(imei => imei.length > 0)));
  }
}
